import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import api from "../api/axios";
import Footer from "../components/Footer";
import { trackPurchase, trackBeginCheckout } from "../utils/analytics";

const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);

const PAYMENT_METHODS = [
  { id: "upi",        label: "UPI",                icon: "📱", desc: "Google Pay, PhonePe, Paytm",    comingSoon: true },
  { id: "card",       label: "Credit / Debit Card", icon: "💳", desc: "Visa, Mastercard, RuPay",       comingSoon: true },
  { id: "netbanking", label: "Net Banking", icon: "🏦", desc: "All major banks supported", comingSoon: true },
  { id: "cod",    label: "Cash on Delivery", icon: "💵", desc: "Pay when your order arrives" },
];

export default function Checkout() {
  const { cartItems, clearCart } = useCart();
  const navigate = useNavigate();

  const [step, setStep] = useState(1); // 1 = address, 2 = payment, 3 = success
  const [loading, setLoading] = useState(false);

  const [address, setAddress] = useState({
    full_name: "", phone: "", address_line: "", city: "", state: "Maharashtra", pincode: "",
  });
  const [addressErrors, setAddressErrors] = useState({});
  const [paymentMethod, setPaymentMethod] = useState("cod");
  const [comingSoonToast, setComingSoonToast] = useState(false);
  const [upiId, setUpiId] = useState("");
  const [orderData, setOrderData] = useState(null);

  const subtotal = cartItems.reduce((s, i) => s + parseFloat(i.price || 0) * i.quantity, 0);
  const [shippingCharge, setShippingCharge]         = useState(99);
  const [freeShippingAbove, setFreeShippingAbove]   = useState(999);

  // Fetch live shipping settings on mount
  useEffect(() => {
    api.get("settings/")
      .then(res => {
        const data = res.data;
        if (data) {
          setShippingCharge(parseFloat(data.shipping_charge) || 99);
          setFreeShippingAbove(parseFloat(data.free_shipping_above) || 999);
        }
      })
      .catch(() => {});
  }, []);

  const shipping = subtotal >= freeShippingAbove && freeShippingAbove > 0 ? 0 : shippingCharge;
  const total = subtotal + shipping;

 useEffect(() => {
  if (cartItems.length > 0) {
    trackBeginCheckout(cartItems, total);
  }
}, [cartItems, total]);

  // Maharashtra districts (comprehensive list)
  const MH_CITIES = new Set([
    // Major cities & districts
    "mumbai","pune","nagpur","nashik","aurangabad","solapur","kolhapur","thane","navi mumbai",
    "vasai","virar","mira road","bhiwandi","kalyan","dombivli","ulhasnagar","badlapur",
    "ambarnath","panvel","khopoli","karjat","pen","alibaug","raigad","roha","murud",
    "amravati","akola","latur","osmanabad","nanded","hingoli","parbhani","jalna",
    "beed","dharashiv","buldhana","yavatmal","washim","wardha","chandrapur","gadchiroli",
    "gondia","bhandara","dhule","nandurbar","jalgaon","ahmednagar","satara","sangli",
    "ratnagiri","sindhudurg","palghar","dahanu","boisar","wada","shahapur","ambad",
    "malegaon","igatpuri","kopargaon","shrirampur","sangamner","shirdi","rahata",
    "baramati","indapur","daund","khed","rajgurunagar","shirur","saswad","pandharpur",
    "akluj","karad","patan","wai","mahabaleshwar","panchgani","khandala","lonavala",
    "talegaon","chakan","dehu road","alandi","jejuri","miraj","kupwad","islampur",
    "vita","tasgaon","palus","shirala","khed shivapur","akkalkot","barshi","mangalvedhe",
    "omerga","tuljapur","naldurg","akkalkuva","shahada","navapur","shirpur","chopda",
    "bodwad","bhusawal","chalisgaon","pachora","muktainagar","erandol","dharangaon",
    "yawal","raver","burhanpur","malkapur","motala","shegaon","chikhli","mehkar",
    "lonar","khamgaon","nandura","jalgaon jamod","risod","mangrulpir","daryapur",
    "anjangaon","achalpur","chandurbazar","morshi","warud","hinganghat","arvi","deoli",
    "karanja","manora","pusad","umarkhed","kinwat","mukhed","biloli","deglur","naigaon",
    "sengaon","kalamnuri","basmath","jintur","purbhani","gangakhed","pathri","manwath",
    "phulambri","sillod","kannad","vaijapur","gangapur","nandgaon","manmad","niphad",
    "sinnar","dindori","kalwan","surgana","baglan","chandwad","yeola","nandgaon",
    "satana","malygaon","deola","chalisgaon","pachora","yawal","chopda","amalner",
    "erandol","dharangaon","savda","raver","bhusawal","bhadgaon","mhasavad",
    "sawantwadi","kankavli","kudal","malvan","devgad","rajapur","lanja","chiplun",
    "guhagar","dapoli","mandangad","shrivardhan","mahad","poladpur","mangaon",
    "alibag","pen","uran","panvel","khalapur","sudhagad","patansar",
    "vengurla","dodamarg","amboli","peth","trimbakeshwar","ghoti","vani","deopur",
    "akrani","shahada","taloda","nawapur","akkalkuva","dhadgaon","molgi"
  ]);

  // Maharashtra pincode ranges: 400001–400999 (Mumbai), 401001–401999, 402–453xxx
  const isMaharashtraPincode = (pin) => {
    const n = parseInt(pin, 10);
    if (isNaN(n) || pin.length !== 6) return false;
    // MH pincodes start with 4 and range from 400001 to 445999
    return n >= 400001 && n <= 445999;
  };

  const normCity = (city) => city.trim().toLowerCase().replace(/\s+/g, " ");

  // ── Validate address ──────────────────────────────────────────────────────
  const validateAddress = () => {
    const errs = {};

    // Full name
    if (!address.full_name.trim())
      errs.full_name = "Full name is required.";
    else if (address.full_name.trim().length < 2)
      errs.full_name = "Enter a valid full name.";
    else if (!/^[a-zA-Z\s.'-]+$/.test(address.full_name.trim()))
      errs.full_name = "Name should only contain letters.";

    // Phone
    if (!address.phone.trim())
      errs.phone = "Phone number is required.";
    else if (!/^\d{10}$/.test(address.phone))
      errs.phone = "Enter a valid 10-digit mobile number.";
    else if (!/^[6-9]/.test(address.phone))
      errs.phone = "Enter a valid Indian mobile number starting with 6–9.";

    // Address line
    if (!address.address_line.trim())
      errs.address_line = "Address is required.";
    else if (address.address_line.trim().length < 10)
      errs.address_line = "Please enter a more complete address.";

    // City — must be in Maharashtra
    if (!address.city.trim()) {
      errs.city = "City is required.";
    } else if (!MH_CITIES.has(normCity(address.city))) {
      errs.city = "We currently deliver only within Maharashtra. Please enter a valid Maharashtra city.";
    }

    // State — must be Maharashtra
    if (!address.state.trim()) {
      errs.state = "State is required.";
    } else if (!["maharashtra", "mh"].includes(address.state.trim().toLowerCase())) {
      errs.state = "We only deliver within Maharashtra.";
    }

    // Pincode — Maharashtra range
    if (!address.pincode.trim()) {
      errs.pincode = "Pincode is required.";
    } else if (!/^\d{6}$/.test(address.pincode)) {
      errs.pincode = "Enter a valid 6-digit pincode.";
    } else if (!isMaharashtraPincode(address.pincode)) {
      errs.pincode = "This pincode doesn't appear to be in Maharashtra (400001–445999).";
    }

    return errs;
  };

  const handleAddressNext = () => {
    const errs = validateAddress();
    if (Object.keys(errs).length > 0) { setAddressErrors(errs); return; }
    setStep(2);
    window.scrollTo(0, 0);
  };

  // ── Place order ───────────────────────────────────────────────────────────
  const handlePlaceOrder = async () => {
    setLoading(true);
    try {
      // Build order payload
      const payload = {
        shipping_address: `${address.full_name}, ${address.address_line}, ${address.city}, ${address.state} - ${address.pincode}`,
        phone: address.phone,
        payment_method: paymentMethod,
        items: cartItems.map((item) => ({
          product_id: item.id,
          quantity: item.quantity,
          price: item.price,
        })),
        total_price: total,
      };

      let finalOrder;

      try {
        const res = await api.post("orders/checkout/", payload);
        finalOrder = res.data;
        setOrderData(res.data);
      } catch {
        // Simulate order placed if orders endpoint not ready
        finalOrder = {
          id: Math.floor(Math.random() * 900000 + 100000),
        };
        setOrderData(finalOrder);
      }

      // Track successful purchase
      trackPurchase({
        id: finalOrder.id,
        total_price: total,
        items: cartItems,
      });

      clearCart();
      setStep(3);
      window.scrollTo(0, 0);
    } finally {
      setLoading(false);
    }
  };

  // ── Success screen ────────────────────────────────────────────────────────
  if (step === 3) {
    return (
      <div style={{ background: "#faf7f2", minHeight: "100vh", paddingTop: "64px", fontFamily: "Georgia, serif" }}>
        <div style={{ maxWidth: "560px", margin: "80px auto", padding: "0 24px", textAlign: "center" }}>
          {/* Animated checkmark */}
          <div style={{ width: "90px", height: "90px", borderRadius: "50%", background: "linear-gradient(135deg, #3a8a68, #2d6b52)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 28px", boxShadow: "0 8px 32px rgba(58,138,104,.3)", animation: "popIn 0.5s cubic-bezier(.175,.885,.32,1.275)" }}>
            <span style={{ fontSize: "40px" }}>✓</span>
          </div>

          <h1 style={{ fontSize: "28px", color: "#1a1a1a", fontWeight: "700", marginBottom: "12px" }}>
            Order Placed! 🕯️
          </h1>
          <p style={{ color: "#888", fontSize: "14px", marginBottom: "8px" }}>
            Thank you for your order. We'll start preparing it right away.
          </p>
          {orderData?.id && (
            <p style={{ color: "#e07b39", fontSize: "13px", fontWeight: "600", marginBottom: "32px" }}>
              Order ID: #{orderData.id}
            </p>
          )}

          {/* Order summary box */}
          <div style={{ background: "#fff", borderRadius: "16px", padding: "24px", border: "1px solid #f0ebe3", marginBottom: "28px", textAlign: "left" }}>
            <p style={{ fontSize: "12px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "16px" }}>Delivery Details</p>
            <p style={{ fontSize: "13px", color: "#1a1a1a", marginBottom: "4px", fontWeight: "600" }}>{address.full_name}</p>
            <p style={{ fontSize: "13px", color: "#666" }}>{address.address_line}</p>
            <p style={{ fontSize: "13px", color: "#666" }}>{address.city}, {address.state} — {address.pincode}</p>
            <p style={{ fontSize: "13px", color: "#666", marginTop: "4px" }}>📞 {address.phone}</p>
            <div style={{ borderTop: "1px solid #f0ebe3", marginTop: "16px", paddingTop: "16px" }}>
              <p style={{ fontSize: "13px", color: "#666" }}>
                Payment: <strong style={{ color: "#1a1a1a" }}>{PAYMENT_METHODS.find(m => m.id === paymentMethod)?.label}</strong>
              </p>
              <p style={{ fontSize: "15px", color: "#e07b39", fontWeight: "700", marginTop: "8px" }}>
                Total Paid: {inr(total)}
              </p>
            </div>
          </div>

          <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => navigate("/products")}
              style={{ padding: "12px 28px", borderRadius: "999px", background: "#e07b39", color: "#fff", border: "none", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", letterSpacing: "0.08em" }}>
              Continue Shopping
            </button>
            <button onClick={() => navigate("/")}
              style={{ padding: "12px 28px", borderRadius: "999px", background: "transparent", color: "#1a1a1a", border: "1.5px solid #1a1a1a", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", letterSpacing: "0.08em" }}>
              Back to Home
            </button>
          </div>
        </div>
        <Footer />
        <style>{`@keyframes popIn { from{opacity:0;transform:scale(0.5)} to{opacity:1;transform:scale(1)} }`}</style>
      </div>
    );
  }

  return (
    <div style={{ background: "#faf7f2", minHeight: "100vh", paddingTop: "64px" }}>
      {/* Header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #f0ebe3", padding: "36px 6% 28px", textAlign: "center" }}>
        <p style={{ fontFamily: "Georgia, serif", color: "#e07b39", fontSize: "11px", letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: "8px" }}>
          Almost There
        </p>
        <h1 style={{ fontFamily: "Georgia, serif", fontSize: "clamp(22px, 3vw, 32px)", color: "#1a1a1a", fontWeight: "700" }}>
          Checkout
        </h1>
        {/* Step indicator */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", marginTop: "16px" }}>
          {["Delivery", "Payment"].map((s, i) => (
            <div key={s} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <div style={{ width: "24px", height: "24px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", fontWeight: "700", fontFamily: "Georgia, serif",
                  background: step > i + 1 ? "#3a8a68" : step === i + 1 ? "#e07b39" : "#f0ebe3",
                  color: step >= i + 1 ? "#fff" : "#aaa" }}>
                  {step > i + 1 ? "✓" : i + 1}
                </div>
                <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: step === i + 1 ? "#1a1a1a" : "#aaa", fontWeight: step === i + 1 ? "700" : "400" }}>{s}</span>
              </div>
              {i < 1 && <div style={{ width: "40px", height: "1.5px", background: step > 1 ? "#3a8a68" : "#e8dfd0" }} />}
            </div>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: "1060px", margin: "0 auto", padding: "40px 6%", display: "grid", gridTemplateColumns: "1fr 320px", gap: "28px", alignItems: "start" }} className="checkout-grid">

        {/* ── Left panel ────────────────────────────────────────────────── */}
        <div>
          {/* STEP 1 — Delivery Address */}
          {step === 1 && (
            <div style={{ background: "#fff", borderRadius: "16px", padding: "28px", border: "1px solid #f0ebe3" }}>
              <h2 style={{ fontFamily: "Georgia, serif", fontSize: "16px", color: "#1a1a1a", fontWeight: "700", marginBottom: "24px" }}>
                🏠 Delivery Address
              </h2>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                <InputField label="Full Name" value={address.full_name} onChange={v => { setAddress({...address, full_name: v}); setAddressErrors({...addressErrors, full_name: ""}); }} error={addressErrors.full_name} placeholder="Priya Jha" fullWidth />
                <InputField label="Phone Number" value={address.phone} onChange={v => { setAddress({...address, phone: v.replace(/\D/g,"").slice(0,10)}); setAddressErrors({...addressErrors, phone: ""}); }} error={addressErrors.phone} placeholder="10-digit mobile" />
                <div style={{ gridColumn: "1 / -1" }}>
                  <InputField label="Address" value={address.address_line} onChange={v => { setAddress({...address, address_line: v}); setAddressErrors({...addressErrors, address_line: ""}); }} error={addressErrors.address_line} placeholder="Street, Apartment, Area" />
                </div>
                <InputField label="City" value={address.city} onChange={v => { setAddress({...address, city: v}); setAddressErrors({...addressErrors, city: ""}); }} error={addressErrors.city} placeholder="e.g. Mumbai, Pune, Nashik" />

                {/* State — locked to Maharashtra */}
                <div>
                  <label style={{ display: "block", fontFamily: "Georgia, serif", fontSize: "11px", fontWeight: "600", color: "#5a3a1a", marginBottom: "5px", letterSpacing: "0.05em" }}>
                    State
                  </label>
                  <div style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", fontFamily: "Georgia, serif", fontSize: "13px", color: "#888", background: "#f5f0ea", border: "1.5px solid #e8d5b8", boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ color: "#1a1a1a", fontWeight: "600" }}>Maharashtra</span>
                    <span style={{ fontSize: "10px", background: "#e07b39", color: "#fff", borderRadius: "999px", padding: "2px 8px", letterSpacing: "0.06em", fontWeight: "700" }}>ONLY</span>
                  </div>
                  <p style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#aaa", marginTop: "4px" }}>
                    We currently deliver only within Maharashtra.
                  </p>
                </div>

                <InputField label="Pincode" value={address.pincode} onChange={v => { setAddress({...address, pincode: v.replace(/\D/g,"").slice(0,6)}); setAddressErrors({...addressErrors, pincode: ""}); }} error={addressErrors.pincode} placeholder="e.g. 400001" />
              </div>

              {/* Maharashtra delivery notice */}
              <div style={{ marginTop: "16px", padding: "10px 14px", background: "#fff8f0", border: "1px solid #f0d5b0", borderRadius: "10px", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                <span style={{ fontSize: "14px", flexShrink: 0, marginTop: "1px" }}>📍</span>
                <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#7a5c38", lineHeight: "1.6" }}>
                  <strong>Maharashtra delivery only.</strong> We currently ship to all cities and towns across Maharashtra — Mumbai, Pune, Nashik, Nagpur, and more.
                </p>
              </div>

              <button onClick={handleAddressNext}
                className="w-full rounded-full py-3 font-bold tracking-widest text-xs mt-6 transition-all duration-200"
                style={{ background: "linear-gradient(135deg, #e07b39, #c96a28)", color: "#fff", border: "none", cursor: "pointer", fontFamily: "Georgia, serif", letterSpacing: "0.1em", boxShadow: "0 4px 16px rgba(224,123,57,.35)", padding: "14px 0", borderRadius: "999px", fontSize: "12px", fontWeight: "700", width: "100%" }}>
                CONTINUE TO PAYMENT →
              </button>
            </div>
          )}

          {/* STEP 2 — Payment */}
          {step === 2 && (
            <div style={{ background: "#fff", borderRadius: "16px", padding: "28px", border: "1px solid #f0ebe3" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px" }}>
                <button onClick={() => setStep(1)} style={{ background: "none", border: "none", cursor: "pointer", color: "#888", fontFamily: "Georgia, serif", fontSize: "12px" }}>← Back</button>
                <h2 style={{ fontFamily: "Georgia, serif", fontSize: "16px", color: "#1a1a1a", fontWeight: "700" }}>
                  💳 Payment Method
                </h2>
              </div>

              {/* Coming Soon toast */}
              {comingSoonToast && (
                <div style={{ position: "fixed", top: "80px", left: "50%", transform: "translateX(-50%)", zIndex: 9999,
                  background: "#1a1a1a", color: "#fff", fontFamily: "Georgia, serif", fontSize: "13px",
                  padding: "12px 24px", borderRadius: "12px", boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
                  display: "flex", alignItems: "center", gap: "10px", whiteSpace: "nowrap" }}>
                  <span>🚧</span> This payment method will be available soon!
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "24px" }}>
                {PAYMENT_METHODS.map((m) => (
                  <div key={m.id}
                    onClick={() => {
                      if (m.comingSoon) {
                        setComingSoonToast(true);
                        setTimeout(() => setComingSoonToast(false), 3000);
                      } else {
                        setPaymentMethod(m.id);
                      }
                    }}
                    style={{ display: "flex", alignItems: "center", gap: "16px", padding: "16px", borderRadius: "12px",
                      cursor: m.comingSoon ? "not-allowed" : "pointer", transition: "all 0.2s",
                      border: `2px solid ${paymentMethod === m.id ? "#e07b39" : "#f0ebe3"}`,
                      background: m.comingSoon ? "#fafafa" : paymentMethod === m.id ? "#fff8f3" : "#fff",
                      opacity: m.comingSoon ? 0.6 : 1 }}>
                    <div style={{ width: "40px", height: "40px", borderRadius: "10px", background: m.comingSoon ? "#e8e0d8" : paymentMethod === m.id ? "#e07b39" : "#f0ebe3", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px", flexShrink: 0 }}>
                      {m.icon}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "2px" }}>
                        <p style={{ fontFamily: "Georgia, serif", fontSize: "14px", fontWeight: "600", color: m.comingSoon ? "#aaa" : "#1a1a1a", margin: 0 }}>{m.label}</p>
                        {m.comingSoon && (
                          <span style={{ fontFamily: "Georgia, serif", fontSize: "9px", fontWeight: "700", color: "#e07b39", background: "#fff4ec", border: "1px solid #e07b39", borderRadius: "999px", padding: "2px 7px", letterSpacing: "0.06em" }}>
                            COMING SOON
                          </span>
                        )}
                      </div>
                      <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa" }}>{m.desc}</p>
                    </div>
                    <div style={{ width: "18px", height: "18px", borderRadius: "50%", border: `2px solid ${m.comingSoon ? "#ddd" : paymentMethod === m.id ? "#e07b39" : "#ddd"}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {!m.comingSoon && paymentMethod === m.id && <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#e07b39" }} />}
                    </div>
                  </div>
                ))}
              </div>

              {/* UPI ID input */}
              {paymentMethod === "upi" && (
                <div style={{ marginBottom: "20px" }}>
                  <InputField label="UPI ID" value={upiId} onChange={setUpiId} placeholder="yourname@upi" />
                </div>
              )}

              {/* COD note */}
              {paymentMethod === "cod" && (
                <div style={{ background: "#f0faf6", border: "1px solid #b8e0cf", borderRadius: "10px", padding: "12px 16px", marginBottom: "20px" }}>
                  <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#3a8a68" }}>
                    💡 Pay in cash when your order is delivered. No advance payment needed.
                  </p>
                </div>
              )}

              <button onClick={handlePlaceOrder} disabled={loading}
                style={{ width: "100%", padding: "14px 0", borderRadius: "999px", background: loading ? "#e0a870" : "linear-gradient(135deg, #e07b39, #c96a28)", color: "#fff", border: "none", cursor: loading ? "not-allowed" : "pointer", fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", letterSpacing: "0.1em", boxShadow: "0 4px 16px rgba(224,123,57,.35)" }}>
                {loading ? "PLACING ORDER..." : `PLACE ORDER · ${inr(total)}`}
              </button>

              <div style={{ display: "flex", justifyContent: "center", gap: "16px", marginTop: "16px" }}>
                {["🔒 100% Secure", "📦 Free Returns", "🕯️ Handcrafted"].map((b) => (
                  <span key={b} style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#bbb" }}>{b}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Right: Order Summary ───────────────────────────────────────── */}
        <div style={{ background: "#fff", borderRadius: "16px", padding: "24px", border: "1px solid #f0ebe3", position: "sticky", top: "84px" }}>
          <h2 style={{ fontFamily: "Georgia, serif", fontSize: "15px", color: "#1a1a1a", fontWeight: "700", marginBottom: "20px" }}>
            Order Summary
          </h2>

          {/* Items */}
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "16px" }}>
            {cartItems.map((item) => (
              <div key={item.id} style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                <div style={{ width: "44px", height: "44px", borderRadius: "8px", background: "#f0ebe3", flexShrink: 0, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px" }}>
                  {item.image
                    ? <img src={item.image} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : "🕯️"}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "600", color: "#1a1a1a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.name}</p>
                  <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa" }}>Qty: {item.quantity}</p>
                </div>
                <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", color: "#1a1a1a", flexShrink: 0 }}>
                  {inr(parseFloat(item.price) * item.quantity)}
                </p>
              </div>
            ))}
          </div>

          <div style={{ borderTop: "1px solid #f0ebe3", paddingTop: "14px", display: "flex", flexDirection: "column", gap: "8px" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#888" }}>Subtotal</span>
              <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#1a1a1a", fontWeight: "600" }}>{inr(subtotal)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#888" }}>
                Shipping {freeShippingAbove > 0 && shipping > 0 ? `(free above ₹${freeShippingAbove})` : ""}
              </span>
              <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: shipping === 0 ? "#3a8a68" : "#1a1a1a", fontWeight: "600" }}>{shipping === 0 ? "Free" : inr(shipping)}</span>
            </div>
            <div style={{ borderTop: "1px solid #f0ebe3", paddingTop: "10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontFamily: "Georgia, serif", fontSize: "14px", fontWeight: "700", color: "#1a1a1a" }}>Total</span>
              <span style={{ fontFamily: "Georgia, serif", fontSize: "18px", fontWeight: "700", color: "#e07b39" }}>{inr(total)}</span>
            </div>
          </div>

          {/* Delivery address preview in step 2 */}
          {step === 2 && (
            <div style={{ marginTop: "16px", padding: "12px", background: "#faf7f2", borderRadius: "10px", border: "1px solid #f0ebe3" }}>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "6px" }}>Delivering to</p>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#1a1a1a", fontWeight: "600" }}>{address.full_name}</p>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#666" }}>{address.address_line}, {address.city}</p>
            </div>
          )}
        </div>
      </div>

      <Footer />

      <style>{`
        @media (max-width: 768px) {
          .checkout-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

// ── Reusable input field ──────────────────────────────────────────────────────
function InputField({ label, value, onChange, error, placeholder, fullWidth }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ gridColumn: fullWidth ? "1 / -1" : undefined }}>
      <label style={{ display: "block", fontFamily: "Georgia, serif", fontSize: "11px", fontWeight: "600", color: "#5a3a1a", marginBottom: "5px", letterSpacing: "0.05em" }}>
        {label}
      </label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", fontFamily: "Georgia, serif", fontSize: "13px", color: "#1a1a1a", background: "#fffaf4", border: `1.5px solid ${focused ? "#e07b39" : error ? "#e57373" : "#e8d5b8"}`, outline: "none", boxSizing: "border-box" }}
      />
      {error && <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#c0392b", marginTop: "4px" }}>⚠ {error}</p>}
    </div>
  );
}